import { useCallback, useEffect, useRef } from "react";
import styled from "@emotion/styled/macro";
import { useCapContext } from "cap/providers/CapContext";
import { InHomeDesign } from "components/molecules/InHomeDesignMolecule";
import { useAppSelector } from "store";
import { IhdMessage } from "utils/IhdMessage";

type RepRedesignDesignRenderProps = {
  offerId: string;
  prospectId: string;
  onDesignComplete: (designId: string) => void;
  onDesignCancel: () => void;
  setError: (error: Error | null) => void;
  copyDesignOrProposalId?: string;
  parentProposalId?: string;
};

const RepRedesignDesignRender = ({
  offerId,
  prospectId,
  onDesignComplete,
  onDesignCancel,
  setError,
  copyDesignOrProposalId,
  parentProposalId,
}: RepRedesignDesignRenderProps) => {
  const { activeProposal, capTask, sfdcUrl } = useCapContext();

  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );

  const iFrameRef = useRef<HTMLIFrameElement>(null);

  const publishToIhd = useCallback((message: IhdMessage) => {
    iFrameRef.current?.contentWindow?.postMessage(message, "*");
  }, []);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const message = event?.data as IhdMessage;
      if (!message) {
        return;
      }

      switch (message.type) {
        case "QUERY:AUTH_TOKEN": {
          if (message.payload.type !== "IHD") return;

          const authMessage = {
            source: "Offer Experience",
            type: "EVENT:AUTH_TOKEN",
            payload: {
              type: "IHD",
              token: cognitoToken,
            },
          } as IhdMessage;

          publishToIhd(authMessage);
          break;
        }
        case "DESIGN_EDIT_COMPLETE": {
          const designId = message.payload?.designId;
          if (!designId) {
            setError(
              new Error("IHD completed without a design id", {
                cause: message.payload,
              })
            );
            return;
          }
          onDesignComplete(designId);
          break;
        }
        case "DESIGN_EDIT_CANCEL": {
          onDesignCancel();
          break;
        }
        case "DESIGN_EDIT_BLOCKED": {
          if (capTask?.id) {
            const capInSfdcUrl = `${sfdcUrl}/apex/wftask_lwc?id=${capTask.id}&sfdc.override=1&c__tabName=DETAILS`;
            window.open(capInSfdcUrl, "_blank", "noopener,noreferrer");
          } else {
            setError(
              new Error("Unable to open CAP task in Salesforce", {
                cause: "CAP task id is not available",
              })
            );
          }
          break;
        }
        case "UNRECOVERABLE_ERROR": {
          const cause = message.payload?.error || "Unknown error";
          setError(new Error("IHD Error", { cause }));
          break;
        }
      }
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, [
    capTask?.id,
    cognitoToken,
    onDesignCancel,
    onDesignComplete,
    publishToIhd,
    setError,
    sfdcUrl,
  ]);

  return (
    <DesignContainer>
      {prospectId && offerId && activeProposal && (
        <InHomeDesign
          ref={iFrameRef}
          offerId={offerId}
          prospectId={prospectId}
          copyDesignOrProposalId={copyDesignOrProposalId}
          parentProposalId={parentProposalId}
        />
      )}
    </DesignContainer>
  );
};

const DesignContainer = styled.section`
  width: 100%;
  height: 100vh;
`;

export { RepRedesignDesignRender };
